import { useState, useEffect, useMemo, useCallback } from 'react'
import InvoiceService from '../services/InvoiceService'
import InventoryService from '../services/InventoryService'
import { exportToCSV } from '../utils/exportCSV'
import {
  FileText, Plus, Trash2, Edit3, Save, X, Search, Eye,
  DollarSign, Clock, CheckCircle, XCircle, Send, CreditCard,
  TrendingUp, BarChart2, Settings, AlertTriangle, Download,
  ClipboardList, ArrowRightCircle, RotateCcw, Receipt, RefreshCw, Shield
} from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import './InvoicesHub.css'

const INVOICE_STATUSES = ['draft', 'sent', 'paid', 'cancelled']
const PAYMENT_METHODS = ['cash', 'transfer', 'mercadopago', 'crypto', 'other']
const INVOICE_TYPES = ['X', 'A', 'B', 'C']
const TAX_CATEGORIES = ['exento', 'iva_21', 'iva_10_5']

const STATUS_LABELS = { draft: 'Borrador', sent: 'Enviada', paid: 'Pagada', cancelled: 'Anulada' }
const STATUS_ICONS = { draft: Edit3, sent: Send, paid: CheckCircle, cancelled: XCircle }
const STATUS_COLORS = { draft: '#94a3b8', sent: '#60a5fa', paid: '#4ade80', cancelled: '#f87171' }

function formatCurrency(n) {
  if (n == null) return '$0'
  return '$' + Number(n).toLocaleString('es-AR', { maximumFractionDigits: 0 })
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

function formatDateInput(d) {
  if (!d) return ''
  return new Date(d).toISOString().split('T')[0]
}

const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

export default function InvoicesHub() {
  const [tab, setTab] = useState('list')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Invoices
  const [invoices, setInvoices] = useState([])
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [selectedItems, setSelectedItems] = useState([])

  // Products (for item picker)
  const [products, setProducts] = useState([])

  // Stock Items (fabricated units for sale)
  const [stockItems, setStockItems] = useState([])

  // Tax config
  const [taxes, setTaxes] = useState([])
  const [taxForm, setTaxForm] = useState({})
  const [editingTaxId, setEditingTaxId] = useState(null)
  const [showTaxForm, setShowTaxForm] = useState(false)

  // Summary
  const [summary, setSummary] = useState(null)
  const [summaryYear, setSummaryYear] = useState(new Date().getFullYear().toString())

  // Quotes (Presupuestos)
  const [quotes, setQuotes] = useState([])
  const [quoteSearchTerm, setQuoteSearchTerm] = useState('')
  const [quoteStatusFilter, setQuoteStatusFilter] = useState('')

  // Credit Notes (Notas de Crédito)
  const [creditNotes, setCreditNotes] = useState([])
  const [cnSearchTerm, setCnSearchTerm] = useState('')
  const [cnStatusFilter, setCnStatusFilter] = useState('')
  const [showCreditNoteForm, setShowCreditNoteForm] = useState(false)
  const [cnRefInvoice, setCnRefInvoice] = useState(null)

  // Payment Receipts (Recibos de Pago)
  const [paymentReceipts, setPaymentReceipts] = useState([])
  const [showReceiptForm, setShowReceiptForm] = useState(false)
  const [receiptRefInvoice, setReceiptRefInvoice] = useState(null)

  // AFIP Integration
  const [afipConfig, setAfipConfig] = useState(null)
  const [afipMeta, setAfipMeta] = useState(null) // voucherTypes, docTypes, ivaConditions

  // Create/Edit invoice
  const [invoiceForm, setInvoiceForm] = useState({
    clientName: '', clientEmail: '', clientAddress: '', clientTaxId: '',
    type: 'X', notes: '', status: 'draft', paymentMethod: '',
    seller: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    items: [{ description: '', quantity: 1, unitPrice: 0, costPrice: 0, taxCategory: 'exento', type: 'product', referenceId: '' }],
  })
  const [editingInvoiceId, setEditingInvoiceId] = useState(null)

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [processing, setProcessing] = useState(false)

  // ── Load data ──────────────────────────────────────────
  const loadInvoices = useCallback(async () => {
    try {
      const res = await InvoiceService.listInvoices()
      setInvoices(res.invoices || [])
    } catch (e) { console.error('loadInvoices:', e) }
  }, [])

  const loadProducts = useCallback(async () => {
    try {
      const res = await InventoryService.listProducts()
      setProducts(res.products || [])
    } catch (e) { console.error('loadProducts:', e) }
  }, [])

  const loadStockItems = useCallback(async () => {
    try {
      const res = await InventoryService.listStockItems({ status: 'available' })
      setStockItems(res.stockItems || [])
    } catch (e) { console.error('loadStockItems:', e) }
  }, [])

  const loadTaxes = useCallback(async () => {
    try {
      const res = await InvoiceService.listTax()
      setTaxes(res.taxes || [])
    } catch (e) { console.error('loadTaxes:', e) }
  }, [])

  const loadQuotes = useCallback(async () => {
    try {
      const res = await InvoiceService.listQuotes()
      setQuotes(res.quotes || [])
    } catch (e) { console.error('loadQuotes:', e) }
  }, [])

  const loadCreditNotes = useCallback(async () => {
    try {
      const res = await InvoiceService.listCreditNotes()
      setCreditNotes(res.creditNotes || [])
    } catch (e) { console.error('loadCreditNotes:', e) }
  }, [])

  const loadPaymentReceipts = useCallback(async () => {
    try {
      const res = await InvoiceService.listPaymentReceipts()
      setPaymentReceipts(res.paymentReceipts || [])
    } catch (e) { console.error('loadPaymentReceipts:', e) }
  }, [])

  const loadAfipConfig = useCallback(async () => {
    try {
      const res = await InvoiceService.getAfipConfig()
      setAfipConfig(res.config || null)
      setAfipMeta({ voucherTypes: res.voucherTypes, docTypes: res.docTypes, ivaConditions: res.ivaConditions })
    } catch (e) { console.error('loadAfipConfig:', e) }
  }, [])

  const loadSummary = useCallback(async () => {
    try {
      const res = await InvoiceService.getSummary({ year: summaryYear })
      setSummary(res)
    } catch (e) { console.error('loadSummary:', e) }
  }, [summaryYear])

  const loadAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await Promise.all([loadInvoices(), loadProducts(), loadStockItems(), loadTaxes(), loadQuotes(), loadCreditNotes(), loadPaymentReceipts(), loadAfipConfig()])
    } catch (e) {
      setError('Error cargando datos de facturación')
    } finally {
      setLoading(false)
    }
  }, [loadInvoices, loadProducts, loadTaxes, loadQuotes, loadCreditNotes, loadPaymentReceipts, loadAfipConfig])

  useEffect(() => { loadAll() }, [loadAll])

  useEffect(() => {
    if (tab === 'summary') loadSummary()
    if (tab === 'receipts') loadPaymentReceipts()
  }, [tab, loadSummary, loadPaymentReceipts])

  // ── Filtered invoices ──────────────────────────────────
  const filteredInvoices = useMemo(() => {
    let list = invoices
    if (statusFilter) list = list.filter(i => i.status === statusFilter)
    if (searchTerm) {
      const s = searchTerm.toLowerCase()
      list = list.filter(i =>
        (i.invoiceNumber || '').toLowerCase().includes(s) ||
        (i.clientName || '').toLowerCase().includes(s) ||
        (i.clientEmail || '').toLowerCase().includes(s)
      )
    }
    return list
  }, [invoices, statusFilter, searchTerm])

  // ── Quick stats ────────────────────────────────────────
  const stats = useMemo(() => {
    const paid = invoices.filter(i => i.status === 'paid')
    const pending = invoices.filter(i => i.status === 'sent' || i.status === 'draft')
    return {
      totalPaid: paid.reduce((s, i) => s + (i.total || 0), 0),
      totalPending: pending.reduce((s, i) => s + (i.total || 0), 0),
      paidCount: paid.length,
      pendingCount: pending.length,
      totalCount: invoices.filter(i => i.status !== 'cancelled').length,
    }
  }, [invoices])

  // ── Overdue invoices ───────────────────────────────────
  const overdueInvoices = useMemo(() => {
    const now = new Date()
    return invoices.filter(i =>
      i.status !== 'paid' && i.status !== 'cancelled' && i.dueDate && new Date(i.dueDate) < now
    )
  }, [invoices])

  // ── Export CSV ─────────────────────────────────────────
  const handleExportCSV = () => {
    const rows = invoices.filter(i => i.status !== 'cancelled')
    exportToCSV(rows, [
      { key: 'invoiceNumber', label: 'Número' },
      { key: 'clientName', label: 'Cliente' },
      { key: 'status', label: 'Estado', format: v => STATUS_LABELS[v] || v },
      { key: 'type', label: 'Tipo' },
      { key: 'subtotal', label: 'Subtotal' },
      { key: 'taxAmount', label: 'Impuestos' },
      { key: 'total', label: 'Total' },
      { key: 'issueDate', label: 'Fecha Emisión', format: v => formatDate(v) },
      { key: 'dueDate', label: 'Vencimiento', format: v => formatDate(v) },
      { key: 'paymentMethod', label: 'Método Pago' },
      { key: 'seller', label: 'Vendedor', format: v => v ? v.charAt(0).toUpperCase() + v.slice(1) : '—' },
    ], 'facturas')
  }

  // ── Filtered quotes ────────────────────────────────────
  const filteredQuotes = useMemo(() => {
    let list = quotes
    if (quoteStatusFilter) list = list.filter(q => q.status === quoteStatusFilter)
    if (quoteSearchTerm) {
      const s = quoteSearchTerm.toLowerCase()
      list = list.filter(q =>
        (q.invoiceNumber || '').toLowerCase().includes(s) ||
        (q.clientName || '').toLowerCase().includes(s)
      )
    }
    return list
  }, [quotes, quoteStatusFilter, quoteSearchTerm])

  // ── Filtered credit notes ──────────────────────────────
  const filteredCreditNotes = useMemo(() => {
    let list = creditNotes
    if (cnStatusFilter) list = list.filter(cn => cn.status === cnStatusFilter)
    if (cnSearchTerm) {
      const s = cnSearchTerm.toLowerCase()
      list = list.filter(cn =>
        (cn.invoiceNumber || '').toLowerCase().includes(s) ||
        (cn.clientName || '').toLowerCase().includes(s) ||
        (cn.notes || '').toLowerCase().includes(s)
      )
    }
    return list
  }, [creditNotes, cnStatusFilter, cnSearchTerm])

  // ── Invoice form helpers ───────────────────────────────
  const taxMap = useMemo(() => {
    const m = {}
    for (const t of taxes) m[t.key] = t
    return m
  }, [taxes])

  const formTotals = useMemo(() => {
    let subtotal = 0, totalTax = 0
    for (const item of invoiceForm.items) {
      const sub = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0)
      const rate = taxMap[item.taxCategory]?.rate || 0
      subtotal += sub
      totalTax += sub * (rate / 100)
    }
    return { subtotal, totalTax, total: subtotal + totalTax }
  }, [invoiceForm.items, taxMap])

  const addItem = () => {
    setInvoiceForm(f => ({
      ...f,
      items: [...f.items, { description: '', quantity: 1, unitPrice: 0, costPrice: 0, taxCategory: 'exento', type: 'product', referenceId: '' }]
    }))
  }

  const removeItem = (idx) => {
    setInvoiceForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))
  }

  const updateItem = (idx, field, value) => {
    setInvoiceForm(f => {
      const items = [...f.items]
      items[idx] = { ...items[idx], [field]: value }
      return { ...f, items }
    })
  }

  const pickProduct = (idx, productId) => {
    const prod = products.find(p => p.$id === productId)
    if (!prod) return
    setInvoiceForm(f => {
      const items = [...f.items]
      items[idx] = {
        ...items[idx],
        description: prod.name,
        unitPrice: prod.salePrice || 0,
        costPrice: prod.costPrice || 0,
        taxCategory: prod.taxCategory || 'exento',
        type: 'product',
        referenceId: prod.$id,
      }
      return { ...f, items }
    })
  }

  const pickStockItem = (idx, stockItemId) => {
    const si = stockItems.find(s => s.$id === stockItemId)
    if (!si) return
    setInvoiceForm(f => {
      const items = [...f.items]
      const label = si.mac ? `${si.deviceModel} (${si.mac})` : si.deviceModel
      items[idx] = {
        ...items[idx],
        description: label,
        unitPrice: si.salePrice || 0,
        costPrice: si.productCost || 0,
        taxCategory: 'exento',
        type: 'stock_item',
        referenceId: si.$id,
      }
      return { ...f, items }
    })
  }

  // ── Invoice CRUD ───────────────────────────────────────
  const handleCreateInvoice = async () => {
    if (!invoiceForm.clientName || !invoiceForm.items.length) return
    setProcessing(true)
    try {
      await InvoiceService.createInvoice({
        ...invoiceForm,
        issueDate: invoiceForm.issueDate ? new Date(invoiceForm.issueDate).toISOString() : new Date().toISOString(),
        dueDate: invoiceForm.dueDate ? new Date(invoiceForm.dueDate).toISOString() : '',
      })
      await loadInvoices()
      setTab('list')
      resetForm()
    } catch (e) {
      alert('Error creando factura: ' + e.message)
    } finally {
      setProcessing(false)
    }
  }

  const handleUpdateInvoice = async () => {
    if (!editingInvoiceId) return
    setProcessing(true)
    try {
      await InvoiceService.updateInvoice(editingInvoiceId, {
        clientName: invoiceForm.clientName,
        clientEmail: invoiceForm.clientEmail,
        clientAddress: invoiceForm.clientAddress,
        clientTaxId: invoiceForm.clientTaxId,
        type: invoiceForm.type,
        notes: invoiceForm.notes,
        paymentMethod: invoiceForm.paymentMethod,
        issueDate: invoiceForm.issueDate ? new Date(invoiceForm.issueDate).toISOString() : undefined,
        dueDate: invoiceForm.dueDate ? new Date(invoiceForm.dueDate).toISOString() : '',
      })
      await loadInvoices()
      setTab('list')
      resetForm()
    } catch (e) {
      alert('Error actualizando factura: ' + e.message)
    } finally {
      setProcessing(false)
    }
  }

  const handlePayInvoice = async (id) => {
    const method = prompt('Método de pago (cash, transfer, mercadopago, crypto, other):', 'transfer')
    if (!method) return
    setProcessing(true)
    try {
      await InvoiceService.payInvoice(id, { paymentMethod: method })
      await loadInvoices()
      if (selectedInvoice?.$id === id) {
        const res = await InvoiceService.getInvoice(id)
        setSelectedInvoice(res.invoice)
      }
    } catch (e) {
      alert('Error: ' + e.message)
    } finally {
      setProcessing(false)
    }
  }

  const handleCancelInvoice = async (id) => {
    if (!confirm('¿Anular esta factura?')) return
    setProcessing(true)
    try {
      await InvoiceService.cancelInvoice(id)
      await loadInvoices()
      if (selectedInvoice?.$id === id) {
        const res = await InvoiceService.getInvoice(id)
        setSelectedInvoice(res.invoice)
      }
    } catch (e) {
      alert('Error: ' + e.message)
    } finally {
      setProcessing(false)
    }
  }

  const handleDeleteInvoice = async (id) => {
    if (!confirm('¿Eliminar esta factura permanentemente?')) return
    setProcessing(true)
    try {
      await InvoiceService.deleteInvoice(id)
      await loadInvoices()
      if (selectedInvoice?.$id === id) {
        setSelectedInvoice(null)
        setSelectedItems([])
      }
    } catch (e) {
      alert('Error: ' + e.message)
    } finally {
      setProcessing(false)
    }
  }

  const handleViewInvoice = async (id) => {
    try {
      const res = await InvoiceService.getInvoice(id)
      setSelectedInvoice(res.invoice)
      setSelectedItems(res.items || [])
      setTab('detail')
    } catch (e) {
      alert('Error cargando factura: ' + e.message)
    }
  }

  const handleEditInvoice = (inv) => {
    setEditingInvoiceId(inv.$id)
    setInvoiceForm({
      clientName: inv.clientName || '',
      clientEmail: inv.clientEmail || '',
      clientAddress: inv.clientAddress || '',
      clientTaxId: inv.clientTaxId || '',
      type: inv.type || 'X',
      notes: inv.notes || '',
      status: inv.status || 'draft',
      paymentMethod: inv.paymentMethod || '',
      seller: inv.seller || '',
      issueDate: formatDateInput(inv.issueDate),
      dueDate: formatDateInput(inv.dueDate),
      items: [],
    })
    setTab('create')
  }

  const resetForm = () => {
    setEditingInvoiceId(null)
    setInvoiceForm({
      clientName: '', clientEmail: '', clientAddress: '', clientTaxId: '',
      type: 'X', notes: '', status: 'draft', paymentMethod: '',
      seller: '',
      issueDate: new Date().toISOString().split('T')[0], dueDate: '',
      items: [{ description: '', quantity: 1, unitPrice: 0, costPrice: 0, taxCategory: 'exento', type: 'product', referenceId: '' }],
    })
  }

  // ── Tax CRUD ───────────────────────────────────────────
  const handleCreateTax = async () => {
    if (!taxForm.key || !taxForm.name || taxForm.rate === undefined) return
    setProcessing(true)
    try {
      await InvoiceService.createTax(taxForm)
      await loadTaxes()
      setShowTaxForm(false)
      setTaxForm({})
    } catch (e) {
      alert('Error: ' + e.message)
    } finally {
      setProcessing(false)
    }
  }

  const handleUpdateTax = async () => {
    if (!editingTaxId) return
    setProcessing(true)
    try {
      await InvoiceService.updateTax(editingTaxId, taxForm)
      await loadTaxes()
      setEditingTaxId(null)
      setTaxForm({})
    } catch (e) {
      alert('Error: ' + e.message)
    } finally {
      setProcessing(false)
    }
  }

  const handleDeleteTax = async (id) => {
    if (!confirm('¿Eliminar esta categoría fiscal?')) return
    setProcessing(true)
    try {
      await InvoiceService.deleteTax(id)
      await loadTaxes()
    } catch (e) {
      alert('Error: ' + e.message)
    } finally {
      setProcessing(false)
    }
  }

  // ── Quote CRUD ─────────────────────────────────────────
  const handleCreateQuote = async () => {
    if (!invoiceForm.clientName || !invoiceForm.items.length) return
    setProcessing(true)
    try {
      await InvoiceService.createQuote({
        ...invoiceForm,
        issueDate: invoiceForm.issueDate ? new Date(invoiceForm.issueDate).toISOString() : new Date().toISOString(),
        validUntil: invoiceForm.dueDate ? new Date(invoiceForm.dueDate).toISOString() : '',
      })
      await loadQuotes()
      setTab('quotes')
      resetForm()
    } catch (e) {
      alert('Error creando presupuesto: ' + e.message)
    } finally {
      setProcessing(false)
    }
  }

  const handleSendQuote = async (id) => {
    setProcessing(true)
    try {
      await InvoiceService.sendQuote(id)
      await loadQuotes()
    } catch (e) { alert('Error: ' + e.message) }
    finally { setProcessing(false) }
  }

  const handleAcceptQuote = async (id) => {
    setProcessing(true)
    try {
      await InvoiceService.acceptQuote(id)
      await loadQuotes()
    } catch (e) { alert('Error: ' + e.message) }
    finally { setProcessing(false) }
  }

  const handleRejectQuote = async (id) => {
    if (!confirm('¿Rechazar este presupuesto?')) return
    setProcessing(true)
    try {
      await InvoiceService.rejectQuote(id)
      await loadQuotes()
    } catch (e) { alert('Error: ' + e.message) }
    finally { setProcessing(false) }
  }

  const handleConvertQuote = async (id) => {
    if (!confirm('¿Convertir este presupuesto en factura?')) return
    setProcessing(true)
    try {
      await InvoiceService.convertQuote(id)
      await Promise.all([loadQuotes(), loadInvoices()])
      setTab('list')
    } catch (e) { alert('Error convirtiendo presupuesto: ' + e.message) }
    finally { setProcessing(false) }
  }

  const handleDeleteQuote = async (id) => {
    if (!confirm('¿Eliminar este presupuesto?')) return
    setProcessing(true)
    try {
      await InvoiceService.deleteQuote(id)
      await loadQuotes()
    } catch (e) { alert('Error: ' + e.message) }
    finally { setProcessing(false) }
  }

  const startNewQuote = () => {
    resetForm()
    setTab('create-quote')
  }

  // ── Credit Note CRUD ───────────────────────────────────
  const handleStartCreditNote = (inv) => {
    setCnRefInvoice(inv)
    setShowCreditNoteForm(true)
    setTab('credit-notes')
  }

  const handleCreateCreditNote = async (refInvoiceId, items, reason) => {
    setProcessing(true)
    try {
      await InvoiceService.createCreditNote({ refInvoiceId, items, reason })
      await loadCreditNotes()
      setShowCreditNoteForm(false)
      setCnRefInvoice(null)
    } catch (e) { alert('Error creando nota de crédito: ' + e.message) }
    finally { setProcessing(false) }
  }

  const handleIssueCreditNote = async (id) => {
    if (!confirm('¿Emitir esta nota de crédito? Esta acción es definitiva.')) return
    setProcessing(true)
    try {
      await InvoiceService.issueCreditNote(id)
      await loadCreditNotes()
    } catch (e) { alert('Error: ' + e.message) }
    finally { setProcessing(false) }
  }

  const handleDeleteCreditNote = async (id) => {
    if (!confirm('¿Eliminar esta nota de crédito?')) return
    setProcessing(true)
    try {
      await InvoiceService.deleteCreditNote(id)
      await loadCreditNotes()
    } catch (e) { alert('Error: ' + e.message) }
    finally { setProcessing(false) }
  }

  // ── Payment Receipts ───────────────────────────────────

  const handleStartReceipt = (invoice) => {
    setReceiptRefInvoice(invoice)
    setShowReceiptForm(true)
    setTab('receipts')
  }

  const handleCreateReceipt = async (data) => {
    setProcessing(true)
    try {
      await InvoiceService.createPaymentReceipt(data)
      setShowReceiptForm(false)
      setReceiptRefInvoice(null)
      await Promise.all([loadPaymentReceipts(), loadInvoices()])
    } catch (e) { alert('Error: ' + e.message) }
    finally { setProcessing(false) }
  }

  const handleDeleteReceipt = async (id) => {
    if (!confirm('¿Eliminar este recibo?')) return
    setProcessing(true)
    try {
      await InvoiceService.deletePaymentReceipt(id)
      await Promise.all([loadPaymentReceipts(), loadInvoices()])
    } catch (e) { alert('Error: ' + e.message) }
    finally { setProcessing(false) }
  }

  // ── Recurring Invoices ─────────────────────────────────

  // AFIP handlers
  const handleSaveAfipConfig = async (data) => {
    setProcessing(true)
    try {
      await InvoiceService.saveAfipConfig(data)
      await loadAfipConfig()
    } catch (e) { alert('Error: ' + e.message) }
    finally { setProcessing(false) }
  }

  const handleValidateAfip = async (invoiceId) => {
    setProcessing(true)
    try {
      const res = await InvoiceService.validateAfipInvoice(invoiceId)
      return res.validation
    } catch (e) { alert('Error: ' + e.message); return null }
    finally { setProcessing(false) }
  }

  // ── Render ─────────────────────────────────────────────
  if (loading) {
    return (
      <div className="fac-page">
        <div className="fac-loading"><div className="fac-spinner" /> Cargando facturación...</div>
      </div>
    )
  }

  return (
    <div className="fac-page">
      <div className="fac-header">
        <div className="fac-header-left">
          <FileText size={28} />
          <div>
            <h1>Facturación</h1>
            <p className="fac-subtitle">{invoices.length} facturas · {formatCurrency(stats.totalPaid)} cobrado</p>
          </div>
        </div>
        <div className="fac-header-actions">
          <button className="fac-btn" onClick={handleExportCSV}>
            <Download size={16} /> Exportar CSV
          </button>
          <button className="fac-btn" onClick={startNewQuote}>
            <ClipboardList size={16} /> Nuevo Presupuesto
          </button>
          <button className="fac-btn fac-btn-primary" onClick={() => { resetForm(); setTab('create') }}>
            <Plus size={16} /> Nueva Factura
          </button>
        </div>
      </div>

      {error && <div className="fac-error"><AlertTriangle size={16} /> {error}</div>}

      <div className="fac-tabs">
        {[
          { key: 'quotes', icon: ClipboardList, label: `Presupuestos (${quotes.length})` },
          { key: 'list', icon: FileText, label: 'Facturas' },
          { key: 'cobranzas', icon: CreditCard, label: `Cobranzas (${overdueInvoices.length})` },
          { key: 'receipts', icon: Receipt, label: `Recibos (${paymentReceipts.length})` },
          { key: 'credit-notes', icon: RotateCcw, label: `Notas Crédito (${creditNotes.length})` },
          { key: 'detail', icon: Eye, label: 'Detalle', disabled: !selectedInvoice },
          { key: 'summary', icon: BarChart2, label: 'Resumen' },
          { key: 'tax', icon: Settings, label: 'Fiscal' },
          { key: 'afip', icon: Shield, label: 'AFIP' },
        ].map(t => (
          <button key={t.key} className={`fac-tab ${tab === t.key ? 'active' : ''}`}
            disabled={t.disabled} onClick={() => setTab(t.key)}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      <div className="fac-content">
        {tab === 'list' && (
          <InvoiceListTab
            invoices={filteredInvoices}
            stats={stats}
            overdueInvoices={overdueInvoices}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            onView={handleViewInvoice}
            onEdit={handleEditInvoice}
            onPay={handlePayInvoice}
            onCancel={handleCancelInvoice}
            onDelete={handleDeleteInvoice}
            onCreditNote={handleStartCreditNote}
            onReceipt={handleStartReceipt}
            processing={processing}
          />
        )}
        {tab === 'cobranzas' && (
          <CobranzasTab invoices={invoices} overdueInvoices={overdueInvoices} paymentReceipts={paymentReceipts} />
        )}
        {tab === 'create' && (
          <InvoiceFormTab
            form={invoiceForm}
            setForm={setInvoiceForm}
            products={products}
            stockItems={stockItems}
            taxes={taxes}
            taxMap={taxMap}
            formTotals={formTotals}
            editingId={editingInvoiceId}
            processing={processing}
            onAddItem={addItem}
            onRemoveItem={removeItem}
            onUpdateItem={updateItem}
            onPickProduct={pickProduct}
            onPickStockItem={pickStockItem}
            onCreate={handleCreateInvoice}
            onUpdate={handleUpdateInvoice}
            onCancel={() => { resetForm(); setTab('list') }}
          />
        )}
        {tab === 'create-quote' && (
          <InvoiceFormTab
            form={invoiceForm}
            setForm={setInvoiceForm}
            products={products}
            stockItems={stockItems}
            taxes={taxes}
            taxMap={taxMap}
            formTotals={formTotals}
            editingId={null}
            processing={processing}
            onAddItem={addItem}
            onRemoveItem={removeItem}
            onUpdateItem={updateItem}
            onPickProduct={pickProduct}
            onPickStockItem={pickStockItem}
            onCreate={handleCreateQuote}
            onUpdate={null}
            onCancel={() => { resetForm(); setTab('quotes') }}
            isQuote
          />
        )}
        {tab === 'quotes' && (
          <QuotesTab
            quotes={filteredQuotes}
            searchTerm={quoteSearchTerm}
            setSearchTerm={setQuoteSearchTerm}
            statusFilter={quoteStatusFilter}
            setStatusFilter={setQuoteStatusFilter}
            onSend={handleSendQuote}
            onAccept={handleAcceptQuote}
            onReject={handleRejectQuote}
            onConvert={handleConvertQuote}
            onDelete={handleDeleteQuote}
            processing={processing}
          />
        )}
        {tab === 'credit-notes' && (
          <CreditNotesTab
            creditNotes={filteredCreditNotes}
            invoices={invoices}
            searchTerm={cnSearchTerm}
            setSearchTerm={setCnSearchTerm}
            statusFilter={cnStatusFilter}
            setStatusFilter={setCnStatusFilter}
            showForm={showCreditNoteForm}
            setShowForm={setShowCreditNoteForm}
            refInvoice={cnRefInvoice}
            setRefInvoice={setCnRefInvoice}
            products={products}
            onIssue={handleIssueCreditNote}
            onDelete={handleDeleteCreditNote}
            onCreate={handleCreateCreditNote}
            processing={processing}
          />
        )}
        {tab === 'detail' && selectedInvoice && (
          <InvoiceDetailTab
            invoice={selectedInvoice}
            items={selectedItems}
            onPay={handlePayInvoice}
            onCancel={handleCancelInvoice}
            processing={processing}
          />
        )}
        {tab === 'receipts' && (
          <PaymentReceiptsTab
            receipts={paymentReceipts}
            invoices={invoices}
            showForm={showReceiptForm}
            setShowForm={setShowReceiptForm}
            refInvoice={receiptRefInvoice}
            setRefInvoice={setReceiptRefInvoice}
            processing={processing}
            onCreateReceipt={handleCreateReceipt}
            onDeleteReceipt={handleDeleteReceipt}
          />
        )}
        {tab === 'summary' && (
          <SummaryTab
            summary={summary}
            year={summaryYear}
            setYear={setSummaryYear}
          />
        )}
        {tab === 'tax' && (
          <TaxConfigTab
            taxes={taxes}
            showForm={showTaxForm}
            setShowForm={setShowTaxForm}
            editingId={editingTaxId}
            setEditingId={setEditingTaxId}
            formData={taxForm}
            setFormData={setTaxForm}
            processing={processing}
            onCreate={handleCreateTax}
            onUpdate={handleUpdateTax}
            onDelete={handleDeleteTax}
          />
        )}
        {tab === 'afip' && (
          <AfipTab
            config={afipConfig}
            meta={afipMeta}
            invoices={invoices}
            processing={processing}
            onSaveConfig={handleSaveAfipConfig}
            onValidate={handleValidateAfip}
          />
        )}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════
// TAB: COBRANZAS (Collections / Aging)
// ═══════════════════════════════════════════════════════════

function CobranzasTab({ invoices, overdueInvoices, paymentReceipts }) {
  const now = new Date()

  // Aging buckets
  const aging = useMemo(() => {
    const pending = invoices.filter(i => i.status !== 'paid' && i.status !== 'cancelled')
    const buckets = { current: [], '1-30': [], '31-60': [], '61-90': [], '90+': [] }
    pending.forEach(inv => {
      if (!inv.dueDate) { buckets.current.push(inv); return }
      const due = new Date(inv.dueDate)
      const days = Math.floor((now - due) / 86400000)
      if (days <= 0) buckets.current.push(inv)
      else if (days <= 30) buckets['1-30'].push(inv)
      else if (days <= 60) buckets['31-60'].push(inv)
      else if (days <= 90) buckets['61-90'].push(inv)
      else buckets['90+'].push(inv)
    })
    return buckets
  }, [invoices])

  const bucketTotal = (list) => list.reduce((s, i) => s + (i.total || 0), 0)

  // Top debtors
  const topDebtors = useMemo(() => {
    const pending = invoices.filter(i => i.status !== 'paid' && i.status !== 'cancelled')
    const byClient = {}
    pending.forEach(inv => {
      const key = inv.clientName || 'Sin nombre'
      if (!byClient[key]) byClient[key] = { name: key, total: 0, count: 0, oldest: null }
      byClient[key].total += inv.total || 0
      byClient[key].count++
      if (inv.dueDate && (!byClient[key].oldest || inv.dueDate < byClient[key].oldest)) {
        byClient[key].oldest = inv.dueDate
      }
    })
    return Object.values(byClient).sort((a, b) => b.total - a.total).slice(0, 10)
  }, [invoices])

  // Payment history for current month
  const monthPayments = useMemo(() => {
    const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    return paymentReceipts.filter(r => r.paymentDate?.startsWith(thisMonth))
  }, [paymentReceipts])

  const totalCollectedMonth = monthPayments.reduce((s, r) => s + (r.amount || 0), 0)

  const BUCKET_META = [
    { key: 'current', label: 'Al día', color: '#4ade80' },
    { key: '1-30', label: '1-30 días', color: '#f59e0b' },
    { key: '31-60', label: '31-60 días', color: '#fb923c' },
    { key: '61-90', label: '61-90 días', color: '#f87171' },
    { key: '90+', label: '90+ días', color: '#ef4444' },
  ]

  return (
    <>
      {/* KPIs */}
      <div className="fac-kpi-row">
        <div className="fac-kpi">
          <DollarSign size={22} className="kpi-icon red" />
          <div>
            <div className="kpi-value">{formatCurrency(bucketTotal(overdueInvoices))}</div>
            <div className="kpi-label">Total Vencido</div>
          </div>
        </div>
        <div className="fac-kpi">
          <Clock size={22} className="kpi-icon yellow" />
          <div>
            <div className="kpi-value">{overdueInvoices.length}</div>
            <div className="kpi-label">Facturas Vencidas</div>
          </div>
        </div>
        <div className="fac-kpi">
          <CreditCard size={22} className="kpi-icon green" />
          <div>
            <div className="kpi-value">{formatCurrency(totalCollectedMonth)}</div>
            <div className="kpi-label">Cobrado este mes</div>
          </div>
        </div>
        <div className="fac-kpi">
          <TrendingUp size={22} className="kpi-icon blue" />
          <div>
            <div className="kpi-value">{topDebtors.length}</div>
            <div className="kpi-label">Clientes c/deuda</div>
          </div>
        </div>
      </div>

      {/* Aging grid */}
      <div className="fac-section-title"><Clock size={18} /> Antigüedad de Deuda</div>
      <div className="fac-aging-grid">
        {BUCKET_META.map(b => (
          <div key={b.key} className="fac-aging-card" style={{ borderTopColor: b.color }}>
            <div className="fac-aging-label">{b.label}</div>
            <div className="fac-aging-amount" style={{ color: b.color }}>{formatCurrency(bucketTotal(aging[b.key]))}</div>
            <div className="fac-aging-count">{aging[b.key].length} factura{aging[b.key].length !== 1 ? 's' : ''}</div>
          </div>
        ))}
      </div>

      {/* Top debtors */}
      <div className="fac-section-title" style={{ marginTop: 24 }}><AlertTriangle size={18} /> Principales Morosos</div>
      {topDebtors.length === 0 ? (
        <div className="fac-empty">No hay clientes con deuda pendiente</div>
      ) : (
        <table className="fac-table">
          <thead><tr><th>Cliente</th><th>Facturas</th><th>Deuda Total</th><th>Más antigua</th></tr></thead>
          <tbody>
            {topDebtors.map(d => (
              <tr key={d.name}>
                <td style={{ fontWeight: 600 }}>{d.name}</td>
                <td>{d.count}</td>
                <td style={{ color: '#f87171', fontWeight: 600 }}>{formatCurrency(d.total)}</td>
                <td>{d.oldest ? formatDate(d.oldest) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

// ═══════════════════════════════════════════════════════════
// TAB: INVOICE LIST
// ═══════════════════════════════════════════════════════════

function InvoiceListTab({ invoices, stats, overdueInvoices, searchTerm, setSearchTerm, statusFilter, setStatusFilter, onView, onEdit, onPay, onCancel, onDelete, onCreditNote, onReceipt, processing }) {
  return (
    <>
      {/* Overdue alerts */}
      {overdueInvoices.length > 0 && (
        <div className="fac-alert-banner">
          <AlertTriangle size={18} />
          <div>
            <strong>{overdueInvoices.length} factura{overdueInvoices.length > 1 ? 's' : ''} vencida{overdueInvoices.length > 1 ? 's' : ''}</strong>
            <span className="fac-alert-detail">
              — Total pendiente: {formatCurrency(overdueInvoices.reduce((s, i) => s + (i.total || 0), 0))}
            </span>
            <div className="fac-alert-list">
              {overdueInvoices.slice(0, 5).map(inv => (
                <span key={inv.$id} className="fac-alert-item" onClick={() => onView(inv.$id)}>
                  {inv.invoiceNumber} · {inv.clientName} · {formatCurrency(inv.total)} · venció {formatDate(inv.dueDate)}
                </span>
              ))}
              {overdueInvoices.length > 5 && <span className="fac-alert-more">+{overdueInvoices.length - 5} más</span>}
            </div>
          </div>
        </div>
      )}

      {/* KPI cards */}
      <div className="fac-kpi-row">
        <div className="fac-kpi">
          <DollarSign size={22} className="kpi-icon green" />
          <div>
            <span className="kpi-value">{formatCurrency(stats.totalPaid)}</span>
            <span className="kpi-label">Cobrado ({stats.paidCount})</span>
          </div>
        </div>
        <div className="fac-kpi">
          <Clock size={22} className="kpi-icon yellow" />
          <div>
            <span className="kpi-value">{formatCurrency(stats.totalPending)}</span>
            <span className="kpi-label">Pendiente ({stats.pendingCount})</span>
          </div>
        </div>
        <div className="fac-kpi">
          <FileText size={22} className="kpi-icon blue" />
          <div>
            <span className="kpi-value">{stats.totalCount}</span>
            <span className="kpi-label">Total Facturas</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="fac-toolbar">
        <div className="fac-search-wrap">
          <Search size={16} />
          <input type="text" placeholder="Buscar por número, cliente..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <div className="fac-filter-wrap">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">Todos los estados</option>
            {INVOICE_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="fac-table-wrap">
        <table className="fac-table">
          <thead>
            <tr>
              <th>Nº</th>
              <th>Fecha</th>
              <th>Cliente</th>
              <th>Tipo</th>
              <th>Subtotal</th>
              <th>IVA</th>
              <th>Total</th>
              <th>Vendedor</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr><td colSpan={10} className="fac-empty">No hay facturas{statusFilter ? ` con estado "${STATUS_LABELS[statusFilter]}"` : ''}.</td></tr>
            ) : (
              invoices.map(inv => {
                const Ico = STATUS_ICONS[inv.status] || FileText
                return (
                  <tr key={inv.$id} className={inv.status === 'cancelled' ? 'cancelled-row' : ''}>
                    <td className="fac-num-cell">{inv.invoiceNumber}</td>
                    <td>{formatDate(inv.issueDate || inv.$createdAt)}</td>
                    <td className="fac-client-cell">{inv.clientName}</td>
                    <td><span className="fac-type-badge">{inv.type || 'X'}</span></td>
                    <td>{formatCurrency(inv.subtotal)}</td>
                    <td>{formatCurrency(inv.taxAmount)}</td>
                    <td className="fac-total-cell">{formatCurrency(inv.total)}</td>
                    <td style={{ fontSize: '12px', color: inv.seller ? '#10b981' : '#64748b' }}>
                      {inv.seller ? inv.seller.charAt(0).toUpperCase() + inv.seller.slice(1) : '—'}
                    </td>
                    <td>
                      <span className="fac-status-badge" style={{ color: STATUS_COLORS[inv.status], borderColor: STATUS_COLORS[inv.status] }}>
                        <Ico size={12} /> {STATUS_LABELS[inv.status]}
                      </span>
                    </td>
                    <td className="fac-actions-cell">
                      <button className="fac-icon-btn" title="Ver" onClick={() => onView(inv.$id)}><Eye size={14} /></button>
                      {inv.status === 'draft' && (
                        <button className="fac-icon-btn" title="Editar" onClick={() => onEdit(inv)}><Edit3 size={14} /></button>
                      )}
                      {(inv.status === 'draft' || inv.status === 'sent') && (
                        <button className="fac-icon-btn success" title="Cobrar" disabled={processing} onClick={() => onPay(inv.$id)}>
                          <CreditCard size={14} />
                        </button>
                      )}
                      {(inv.status === 'sent' || inv.status === 'draft') && (
                        <button className="fac-icon-btn" title="Registrar Pago" disabled={processing} onClick={() => onReceipt(inv)}>
                          <Receipt size={14} />
                        </button>
                      )}
                      {inv.status !== 'cancelled' && inv.status !== 'paid' && (
                        <button className="fac-icon-btn warning" title="Anular" disabled={processing} onClick={() => onCancel(inv.$id)}>
                          <XCircle size={14} />
                        </button>
                      )}
                      {inv.status === 'paid' && (
                        <button className="fac-icon-btn primary" title="Nota de Crédito" disabled={processing} onClick={() => onCreditNote(inv)}>
                          <RotateCcw size={14} />
                        </button>
                      )}
                      {inv.status === 'draft' && (
                        <button className="fac-icon-btn danger" title="Eliminar" disabled={processing} onClick={() => onDelete(inv.$id)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ═══════════════════════════════════════════════════════════
// TAB: CREATE / EDIT INVOICE
// ═══════════════════════════════════════════════════════════

function InvoiceFormTab({ form, setForm, products, stockItems = [], taxes, taxMap, formTotals, editingId, processing, onAddItem, onRemoveItem, onUpdateItem, onPickProduct, onPickStockItem, onCreate, onUpdate, onCancel, isQuote }) {
  const docLabel = isQuote ? 'Presupuesto' : 'Factura'
  const availableStockItems = stockItems.filter(si => si.status === 'available')
  return (
    <>
      <div className="fac-form-section">
        <h3>{editingId ? `Editar ${docLabel}` : `${isQuote ? 'Nuevo' : 'Nueva'} ${docLabel}`}</h3>

        {/* Client info */}
        <div className="fac-form-grid">
          <div className="fac-form-group">
            <label>Cliente *</label>
            <input type="text" placeholder="Nombre del cliente" value={form.clientName}
              onChange={e => setForm(f => ({ ...f, clientName: e.target.value }))} />
          </div>
          <div className="fac-form-group">
            <label>Email</label>
            <input type="email" placeholder="cliente@email.com" value={form.clientEmail}
              onChange={e => setForm(f => ({ ...f, clientEmail: e.target.value }))} />
          </div>
          <div className="fac-form-group">
            <label>CUIT / DNI</label>
            <input type="text" placeholder="20-12345678-9" value={form.clientTaxId}
              onChange={e => setForm(f => ({ ...f, clientTaxId: e.target.value }))} />
          </div>
          <div className="fac-form-group">
            <label>Dirección</label>
            <input type="text" placeholder="Dirección" value={form.clientAddress}
              onChange={e => setForm(f => ({ ...f, clientAddress: e.target.value }))} />
          </div>
          <div className="fac-form-group">
            <label>Tipo Factura</label>
            <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
              {INVOICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="fac-form-group">
            <label>Estado</label>
            <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              <option value="draft">Borrador</option>
              <option value="sent">Enviada</option>
            </select>
          </div>
          <div className="fac-form-group">
            <label>Fecha Emisión</label>
            <input type="date" value={form.issueDate} onChange={e => setForm(f => ({ ...f, issueDate: e.target.value }))} />
          </div>
          <div className="fac-form-group">
            <label>{isQuote ? 'Válido Hasta' : 'Fecha Vencimiento'}</label>
            <input type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} />
          </div>
          <div className="fac-form-group">
            <label>Vendedor</label>
            <select value={form.seller} onChange={e => setForm(f => ({ ...f, seller: e.target.value }))}>
              <option value="">— Sin asignar —</option>
              <option value="agustin">Agustín</option>
              <option value="martin">Martín</option>
              <option value="pipi">Pipi</option>
            </select>
          </div>
        </div>

        {/* Items */}
        <div className="fac-items-section">
          <div className="fac-items-header">
            <h4>Ítems</h4>
            <button className="fac-btn fac-btn-sm" onClick={onAddItem}><Plus size={14} /> Agregar Ítem</button>
          </div>

          <div className="fac-items-table-wrap">
            <table className="fac-items-table">
              <thead>
                <tr>
                  <th className="col-product">Tipo / Producto</th>
                  <th className="col-desc">Descripción</th>
                  <th className="col-qty">Cant</th>
                  <th className="col-price">Precio</th>
                  <th className="col-cost">Costo</th>
                  <th className="col-tax">Fiscal</th>
                  <th className="col-subtotal">Subtotal</th>
                  <th className="col-actions"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((item, idx) => {
                  const sub = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0)
                  return (
                    <tr key={idx}>
                      <td style={{ minWidth: 160 }}>
                        <select value={item.type}
                          onChange={e => onUpdateItem(idx, 'type', e.target.value)}
                          style={{ marginBottom: 4, fontSize: '0.8em' }}>
                          <option value="product">Producto catalog.</option>
                          <option value="stock_item">Stock Fabricado</option>
                        </select>
                        {item.type === 'stock_item' ? (
                          <select value={item.referenceId}
                            onChange={e => e.target.value ? onPickStockItem(idx, e.target.value) : onUpdateItem(idx, 'referenceId', '')}>
                            <option value="">— Seleccionar —</option>
                            {availableStockItems.map(si => (
                              <option key={si.$id} value={si.$id}>
                                {si.deviceModel}{si.mac ? ` (${si.mac})` : ''}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <select value={item.referenceId}
                            onChange={e => e.target.value ? onPickProduct(idx, e.target.value) : onUpdateItem(idx, 'referenceId', '')}>
                            <option value="">Manual</option>
                            {products.filter(p => p.active !== false).map(p => (
                              <option key={p.$id} value={p.$id}>{p.name} ({p.stock})</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td>
                        <input type="text" placeholder="Descripción" value={item.description}
                          onChange={e => onUpdateItem(idx, 'description', e.target.value)} />
                      </td>
                      <td>
                        <input type="number" min="1" value={item.quantity}
                          onChange={e => onUpdateItem(idx, 'quantity', Number(e.target.value))} />
                      </td>
                      <td>
                        <input type="number" min="0" step="0.01" value={item.unitPrice}
                          onChange={e => onUpdateItem(idx, 'unitPrice', Number(e.target.value))} />
                      </td>
                      <td>
                        <input type="number" min="0" step="0.01" value={item.costPrice}
                          onChange={e => onUpdateItem(idx, 'costPrice', Number(e.target.value))} />
                      </td>
                      <td>
                        <select value={item.taxCategory}
                          onChange={e => onUpdateItem(idx, 'taxCategory', e.target.value)}>
                          {TAX_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td className="fac-item-subtotal">{formatCurrency(sub)}</td>
                      <td>
                        <button className="fac-icon-btn danger" onClick={() => onRemoveItem(idx)}><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="fac-totals">
            <div className="fac-total-row"><span>Subtotal</span><span>{formatCurrency(formTotals.subtotal)}</span></div>
            <div className="fac-total-row"><span>Impuestos</span><span>{formatCurrency(formTotals.totalTax)}</span></div>
            <div className="fac-total-row total"><span>Total</span><span>{formatCurrency(formTotals.total)}</span></div>
          </div>
        </div>

        {/* Notes */}
        <div className="fac-form-group fac-form-full">
          <label>Notas</label>
          <textarea placeholder="Observaciones..." value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
        </div>

        {/* Actions */}
        <div className="fac-form-actions">
          <button className="fac-btn" onClick={onCancel}><X size={14} /> Cancelar</button>
          <button className="fac-btn fac-btn-primary" disabled={processing || !form.clientName || !form.items.length}
            onClick={editingId ? onUpdate : onCreate}>
            <Save size={14} /> {editingId ? 'Guardar' : `Crear ${docLabel}`}
          </button>
        </div>
      </div>
    </>
  )
}

// ═══════════════════════════════════════════════════════════
// TAB: QUOTES (PRESUPUESTOS)
// ═══════════════════════════════════════════════════════════

const QUOTE_STATUS_LABELS = {
  quote_draft: 'Borrador', quote_sent: 'Enviado', quote_accepted: 'Aceptado',
  quote_rejected: 'Rechazado', quote_converted: 'Convertido',
}
const QUOTE_STATUS_COLORS = {
  quote_draft: '#94a3b8', quote_sent: '#60a5fa', quote_accepted: '#4ade80',
  quote_rejected: '#f87171', quote_converted: '#a78bfa',
}
const QUOTE_STATUSES = ['quote_draft', 'quote_sent', 'quote_accepted', 'quote_rejected', 'quote_converted']

function QuotesTab({ quotes, searchTerm, setSearchTerm, statusFilter, setStatusFilter, onSend, onAccept, onReject, onConvert, onDelete, processing }) {
  const pendingQuotes = quotes.filter(q => q.status === 'quote_draft' || q.status === 'quote_sent')
  const totalPending = pendingQuotes.reduce((s, q) => s + (q.total || 0), 0)
  const acceptedTotal = quotes.filter(q => q.status === 'quote_accepted' || q.status === 'quote_converted').reduce((s, q) => s + (q.total || 0), 0)

  return (
    <>
      {/* KPIs */}
      <div className="fac-kpis">
        <div className="fac-kpi">
          <ClipboardList size={20} />
          <div><span className="fac-kpi-value">{quotes.length}</span><span className="fac-kpi-label">Total</span></div>
        </div>
        <div className="fac-kpi">
          <Clock size={20} />
          <div><span className="fac-kpi-value">{pendingQuotes.length}</span><span className="fac-kpi-label">Pendientes</span></div>
        </div>
        <div className="fac-kpi">
          <DollarSign size={20} />
          <div><span className="fac-kpi-value">{formatCurrency(totalPending)}</span><span className="fac-kpi-label">Monto Pend.</span></div>
        </div>
        <div className="fac-kpi">
          <CheckCircle size={20} />
          <div><span className="fac-kpi-value">{formatCurrency(acceptedTotal)}</span><span className="fac-kpi-label">Aceptados</span></div>
        </div>
      </div>

      {/* Filters */}
      <div className="fac-filters">
        <div className="fac-search-wrap">
          <Search size={16} />
          <input type="text" placeholder="Buscar presupuesto..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">Todos los estados</option>
          {QUOTE_STATUSES.map(s => <option key={s} value={s}>{QUOTE_STATUS_LABELS[s]}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="fac-table-wrap">
        <table className="fac-table">
          <thead>
            <tr>
              <th>Número</th>
              <th>Cliente</th>
              <th>Total</th>
              <th>Fecha</th>
              <th>Válido Hasta</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {quotes.length === 0 ? (
              <tr><td colSpan={7} className="fac-empty">No hay presupuestos.</td></tr>
            ) : (
              quotes.map(q => (
                <tr key={q.$id}>
                  <td className="fac-num-cell">{q.invoiceNumber}</td>
                  <td>{q.clientName}</td>
                  <td className="fac-amount-cell">{formatCurrency(q.total)}</td>
                  <td>{formatDate(q.issueDate || q.$createdAt)}</td>
                  <td>{formatDate(q.dueDate)}</td>
                  <td>
                    <span className="fac-status-badge" style={{ background: QUOTE_STATUS_COLORS[q.status] || '#64748b' }}>
                      {QUOTE_STATUS_LABELS[q.status] || q.status}
                    </span>
                  </td>
                  <td className="fac-actions-cell">
                    {q.status === 'quote_draft' && (
                      <button className="fac-icon-btn" title="Enviar al cliente" disabled={processing} onClick={() => onSend(q.$id)}>
                        <Send size={14} />
                      </button>
                    )}
                    {(q.status === 'quote_draft' || q.status === 'quote_sent') && (
                      <button className="fac-icon-btn success" title="Aceptar" disabled={processing} onClick={() => onAccept(q.$id)}>
                        <CheckCircle size={14} />
                      </button>
                    )}
                    {(q.status === 'quote_draft' || q.status === 'quote_sent') && (
                      <button className="fac-icon-btn danger" title="Rechazar" disabled={processing} onClick={() => onReject(q.$id)}>
                        <XCircle size={14} />
                      </button>
                    )}
                    {q.status === 'quote_accepted' && (
                      <button className="fac-icon-btn primary" title="Convertir a Factura" disabled={processing} onClick={() => onConvert(q.$id)}>
                        <ArrowRightCircle size={14} />
                      </button>
                    )}
                    {q.status !== 'quote_converted' && (
                      <button className="fac-icon-btn danger" title="Eliminar" disabled={processing} onClick={() => onDelete(q.$id)}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ═══════════════════════════════════════════════════════════
// TAB: CREDIT NOTES (NOTAS DE CRÉDITO)
// ═══════════════════════════════════════════════════════════

const CN_STATUS_LABELS = { credit_draft: 'Borrador', credit_issued: 'Emitida' }
const CN_STATUS_COLORS = { credit_draft: '#94a3b8', credit_issued: '#4ade80' }

function CreditNotesTab({ creditNotes, invoices, searchTerm, setSearchTerm, statusFilter, setStatusFilter,
  showForm, setShowForm, refInvoice, setRefInvoice, products, onIssue, onDelete, onCreate, processing }) {

  const [cnItems, setCnItems] = useState([])
  const [cnReason, setCnReason] = useState('')
  const [selectedInvId, setSelectedInvId] = useState('')

  const paidInvoices = invoices.filter(i => i.status === 'paid')

  const startFromInvoice = (inv) => {
    setRefInvoice(inv)
    setCnReason(`Nota de crédito ref. ${inv.invoiceNumber}`)
    setCnItems([{ description: `Devolución - ${inv.invoiceNumber}`, quantity: 1, unitPrice: inv.total || 0, costPrice: 0, taxCategory: 'exento', type: 'product', referenceId: '' }])
    setShowForm(true)
  }

  const handleSelectInvoice = (invId) => {
    setSelectedInvId(invId)
    const inv = invoices.find(i => i.$id === invId)
    if (inv) startFromInvoice(inv)
  }

  useEffect(() => {
    if (refInvoice && cnItems.length === 0) {
      startFromInvoice(refInvoice)
    }
  }, [refInvoice])

  const totalCreditNotes = creditNotes.filter(cn => cn.status === 'credit_issued').reduce((s, cn) => s + (cn.total || 0), 0)

  return (
    <>
      {/* KPIs */}
      <div className="fac-kpis">
        <div className="fac-kpi">
          <RotateCcw size={20} />
          <div><span className="fac-kpi-value">{creditNotes.length}</span><span className="fac-kpi-label">Total NC</span></div>
        </div>
        <div className="fac-kpi">
          <DollarSign size={20} />
          <div><span className="fac-kpi-value">{formatCurrency(totalCreditNotes)}</span><span className="fac-kpi-label">Total Emitido</span></div>
        </div>
      </div>

      {/* Create form */}
      {showForm ? (
        <div className="fac-form-section">
          <h3>Nueva Nota de Crédito</h3>
          {!refInvoice && (
            <div className="fac-form-grid">
              <div className="fac-form-group">
                <label>Factura de referencia *</label>
                <select value={selectedInvId} onChange={e => handleSelectInvoice(e.target.value)}>
                  <option value="">Seleccionar factura...</option>
                  {paidInvoices.map(inv => (
                    <option key={inv.$id} value={inv.$id}>{inv.invoiceNumber} — {inv.clientName} — {formatCurrency(inv.total)}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {refInvoice && (
            <>
              <div className="fac-cn-ref">
                <strong>Referencia:</strong> {refInvoice.invoiceNumber} — {refInvoice.clientName} — {formatCurrency(refInvoice.total)}
              </div>
              <div className="fac-form-grid">
                <div className="fac-form-group" style={{gridColumn: '1 / -1'}}>
                  <label>Motivo</label>
                  <input type="text" value={cnReason} onChange={e => setCnReason(e.target.value)} placeholder="Motivo de la nota de crédito" />
                </div>
              </div>
              <h4>Ítems a acreditar</h4>
              {cnItems.map((item, idx) => (
                <div key={idx} className="fac-form-grid" style={{ marginBottom: 8 }}>
                  <div className="fac-form-group">
                    <label>Descripción</label>
                    <input type="text" value={item.description}
                      onChange={e => { const ni = [...cnItems]; ni[idx] = { ...ni[idx], description: e.target.value }; setCnItems(ni) }} />
                  </div>
                  <div className="fac-form-group">
                    <label>Cantidad</label>
                    <input type="number" min="1" value={item.quantity}
                      onChange={e => { const ni = [...cnItems]; ni[idx] = { ...ni[idx], quantity: Number(e.target.value) }; setCnItems(ni) }} />
                  </div>
                  <div className="fac-form-group">
                    <label>Precio Unit.</label>
                    <input type="number" min="0" step="0.01" value={item.unitPrice}
                      onChange={e => { const ni = [...cnItems]; ni[idx] = { ...ni[idx], unitPrice: Number(e.target.value) }; setCnItems(ni) }} />
                  </div>
                  <div className="fac-form-group" style={{display: 'flex', alignItems: 'flex-end'}}>
                    {cnItems.length > 1 && (
                      <button className="fac-icon-btn danger" onClick={() => setCnItems(cnItems.filter((_, i) => i !== idx))}><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>
              ))}
              <button className="fac-btn" style={{marginBottom: 12}} onClick={() => setCnItems([...cnItems, { description: '', quantity: 1, unitPrice: 0, costPrice: 0, taxCategory: 'exento', type: 'product', referenceId: '' }])}>
                <Plus size={14} /> Agregar ítem
              </button>
              <div className="fac-cn-total">
                <strong>Total NC:</strong> {formatCurrency(cnItems.reduce((s, it) => s + (it.quantity * it.unitPrice), 0))}
              </div>
              <div className="fac-form-actions">
                <button className="fac-btn" onClick={() => { setShowForm(false); setRefInvoice(null); setCnItems([]); setCnReason('') }}>
                  <X size={14} /> Cancelar
                </button>
                <button className="fac-btn fac-btn-primary" disabled={processing || !cnItems.length || !refInvoice}
                  onClick={() => onCreate(refInvoice.$id, cnItems, cnReason)}>
                  <Save size={14} /> Crear Nota de Crédito
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div style={{marginBottom: 16}}>
          <button className="fac-btn fac-btn-primary" onClick={() => setShowForm(true)}>
            <Plus size={16} /> Nueva Nota de Crédito
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="fac-filters">
        <div className="fac-search-wrap">
          <Search size={16} />
          <input type="text" placeholder="Buscar nota de crédito..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="credit_draft">Borrador</option>
          <option value="credit_issued">Emitida</option>
        </select>
      </div>

      {/* Table */}
      <div className="fac-table-wrap">
        <table className="fac-table">
          <thead>
            <tr>
              <th>Número</th>
              <th>Cliente</th>
              <th>Referencia</th>
              <th>Total</th>
              <th>Fecha</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {creditNotes.length === 0 ? (
              <tr><td colSpan={7} className="fac-empty">No hay notas de crédito.</td></tr>
            ) : (
              creditNotes.map(cn => (
                <tr key={cn.$id}>
                  <td className="fac-num-cell">{cn.invoiceNumber}</td>
                  <td>{cn.clientName}</td>
                  <td className="fac-ref-cell">{cn.notes}</td>
                  <td className="fac-amount-cell" style={{color: '#f87171'}}>{formatCurrency(cn.total)}</td>
                  <td>{formatDate(cn.issueDate || cn.$createdAt)}</td>
                  <td>
                    <span className="fac-status-badge" style={{ background: CN_STATUS_COLORS[cn.status] || '#64748b' }}>
                      {CN_STATUS_LABELS[cn.status] || cn.status}
                    </span>
                  </td>
                  <td className="fac-actions-cell">
                    {cn.status === 'credit_draft' && (
                      <button className="fac-icon-btn success" title="Emitir" disabled={processing} onClick={() => onIssue(cn.$id)}>
                        <CheckCircle size={14} />
                      </button>
                    )}
                    {cn.status === 'credit_draft' && (
                      <button className="fac-icon-btn danger" title="Eliminar" disabled={processing} onClick={() => onDelete(cn.$id)}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ═══════════════════════════════════════════════════════════
// TAB: INVOICE DETAIL
// ═══════════════════════════════════════════════════════════

function InvoiceDetailTab({ invoice, items, onPay, onCancel, processing }) {
  const Ico = STATUS_ICONS[invoice.status] || FileText

  return (
    <div className="fac-detail">
      {/* Header */}
      <div className="fac-detail-header">
        <div>
          <h2 className="fac-detail-number">{invoice.invoiceNumber}</h2>
          <span className="fac-detail-type">Factura tipo {invoice.type || 'X'}</span>
        </div>
        <div className="fac-detail-status">
          <span className="fac-status-badge lg" style={{ color: STATUS_COLORS[invoice.status], borderColor: STATUS_COLORS[invoice.status] }}>
            <Ico size={16} /> {STATUS_LABELS[invoice.status]}
          </span>
          {(invoice.status === 'draft' || invoice.status === 'sent') && (
            <button className="fac-btn fac-btn-success" disabled={processing} onClick={() => onPay(invoice.$id)}>
              <CreditCard size={14} /> Cobrar
            </button>
          )}
          {invoice.status !== 'cancelled' && invoice.status !== 'paid' && (
            <button className="fac-btn fac-btn-danger" disabled={processing} onClick={() => onCancel(invoice.$id)}>
              <XCircle size={14} /> Anular
            </button>
          )}
        </div>
      </div>

      {/* Client & dates */}
      <div className="fac-detail-grid">
        <div className="fac-detail-card">
          <h4>Cliente</h4>
          <p className="fac-detail-name">{invoice.clientName}</p>
          {invoice.clientEmail && <p>{invoice.clientEmail}</p>}
          {invoice.clientTaxId && <p>CUIT: {invoice.clientTaxId}</p>}
          {invoice.clientAddress && <p>{invoice.clientAddress}</p>}
        </div>
        <div className="fac-detail-card">
          <h4>Fechas</h4>
          <p>Emisión: {formatDate(invoice.issueDate || invoice.$createdAt)}</p>
          {invoice.dueDate && <p>Vencimiento: {formatDate(invoice.dueDate)}</p>}
          {invoice.paidDate && <p>Cobro: {formatDate(invoice.paidDate)}</p>}
          {invoice.paymentMethod && <p>Método: {invoice.paymentMethod}</p>}
        </div>
      </div>

      {/* Items table */}
      <div className="fac-table-wrap">
        <table className="fac-table">
          <thead>
            <tr>
              <th>Descripción</th>
              <th>Cant</th>
              <th>Precio Unit.</th>
              <th>Subtotal</th>
              <th>IVA</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it.$id || i}>
                <td>{it.description}</td>
                <td>{it.quantity}</td>
                <td>{formatCurrency(it.unitPrice)}</td>
                <td>{formatCurrency(it.subtotal)}</td>
                <td>{formatCurrency(it.taxAmount)}</td>
                <td className="fac-total-cell">{formatCurrency(it.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="fac-totals">
        <div className="fac-total-row"><span>Subtotal</span><span>{formatCurrency(invoice.subtotal)}</span></div>
        <div className="fac-total-row"><span>IVA</span><span>{formatCurrency(invoice.taxAmount)}</span></div>
        <div className="fac-total-row total"><span>Total</span><span>{formatCurrency(invoice.total)}</span></div>
      </div>

      {invoice.notes && (
        <div className="fac-detail-notes">
          <h4>Notas</h4>
          <p>{invoice.notes}</p>
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════
// TAB: SUMMARY
// ═══════════════════════════════════════════════════════════

function SummaryTab({ summary, year, setYear }) {
  if (!summary) {
    return <div className="fac-loading"><div className="fac-spinner" /> Cargando resumen...</div>
  }

  const chartData = Object.entries(summary.monthly || {}).map(([key, val]) => {
    const m = parseInt(key.split('-')[1]) - 1
    return { name: MONTH_NAMES[m], revenue: val.revenue, tax: val.tax, pending: val.pending, count: val.count }
  })

  return (
    <>
      <div className="fac-toolbar">
        <h3>Resumen Anual</h3>
        <div className="fac-filter-wrap">
          <select value={year} onChange={e => setYear(e.target.value)}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <div className="fac-kpi-row">
        <div className="fac-kpi">
          <DollarSign size={22} className="kpi-icon green" />
          <div>
            <span className="kpi-value">{formatCurrency(summary.totalRevenue)}</span>
            <span className="kpi-label">Facturado</span>
          </div>
        </div>
        <div className="fac-kpi">
          <Clock size={22} className="kpi-icon yellow" />
          <div>
            <span className="kpi-value">{formatCurrency(summary.totalPending)}</span>
            <span className="kpi-label">Pendiente</span>
          </div>
        </div>
        <div className="fac-kpi">
          <TrendingUp size={22} className="kpi-icon blue" />
          <div>
            <span className="kpi-value">{formatCurrency(summary.grossMargin)}</span>
            <span className="kpi-label">Margen Bruto</span>
          </div>
        </div>
        <div className="fac-kpi">
          <Settings size={22} className="kpi-icon red" />
          <div>
            <span className="kpi-value">{formatCurrency(summary.totalTax)}</span>
            <span className="kpi-label">Impuestos</span>
          </div>
        </div>
      </div>

      {/* Monthly chart */}
      <div className="fac-chart-card">
        <h4>Facturación Mensual</h4>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
            <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
            <Tooltip
              contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
              labelStyle={{ color: '#f1f5f9' }}
              formatter={(v) => formatCurrency(v)}
            />
            <Bar dataKey="revenue" name="Cobrado" fill="#4ade80" radius={[4, 4, 0, 0]} />
            <Bar dataKey="pending" name="Pendiente" fill="#fbbf24" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Tax breakdown */}
      {summary.taxBreakdown && Object.keys(summary.taxBreakdown).length > 0 && (
        <div className="fac-tax-summary">
          <h4>Desglose Fiscal</h4>
          <table className="fac-table">
            <thead>
              <tr>
                <th>Categoría</th>
                <th>Cantidad</th>
                <th>Base Imponible</th>
                <th>IVA</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(summary.taxBreakdown).map(([cat, data]) => (
                <tr key={cat}>
                  <td><span className="fac-type-badge">{cat}</span></td>
                  <td>{data.count}</td>
                  <td>{formatCurrency(data.subtotal)}</td>
                  <td>{formatCurrency(data.tax)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

// ═══════════════════════════════════════════════════════════
// TAB: TAX CONFIG
// ═══════════════════════════════════════════════════════════

function TaxConfigTab({ taxes, showForm, setShowForm, editingId, setEditingId, formData, setFormData, processing, onCreate, onUpdate, onDelete }) {
  return (
    <>
      <div className="fac-toolbar">
        <h3>Configuración Fiscal</h3>
        <button className="fac-btn fac-btn-primary" onClick={() => {
          setShowForm(!showForm)
          setEditingId(null)
          setFormData({ type: 'iva', active: true, rate: 0 })
        }}>
          <Plus size={16} /> Nueva Categoría
        </button>
      </div>

      {(showForm || editingId) && (
        <div className="fac-form-section compact">
          <h4>{editingId ? 'Editar Categoría' : 'Nueva Categoría Fiscal'}</h4>
          <div className="fac-form-grid">
            <div className="fac-form-group">
              <label>Clave *</label>
              <input type="text" placeholder="iva_21" value={formData.key || ''}
                onChange={e => setFormData(p => ({ ...p, key: e.target.value }))} />
            </div>
            <div className="fac-form-group">
              <label>Nombre *</label>
              <input type="text" placeholder="IVA 21%" value={formData.name || ''}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="fac-form-group">
              <label>Tasa (%)</label>
              <input type="number" min="0" max="100" step="0.01" value={formData.rate ?? 0}
                onChange={e => setFormData(p => ({ ...p, rate: Number(e.target.value) }))} />
            </div>
            <div className="fac-form-group">
              <label>Tipo</label>
              <select value={formData.type || 'iva'}
                onChange={e => setFormData(p => ({ ...p, type: e.target.value }))}>
                <option value="iva">IVA</option>
                <option value="percepcion">Percepción</option>
                <option value="retencion">Retención</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <div className="fac-form-group fac-form-full">
              <label>Notas</label>
              <input type="text" placeholder="Notas..." value={formData.notes || ''}
                onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <div className="fac-form-actions">
            <button className="fac-btn" onClick={() => { setShowForm(false); setEditingId(null); setFormData({}) }}>
              <X size={14} /> Cancelar
            </button>
            <button className="fac-btn fac-btn-primary" onClick={editingId ? onUpdate : onCreate} disabled={processing}>
              <Save size={14} /> {editingId ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </div>
      )}

      <div className="fac-table-wrap">
        <table className="fac-table">
          <thead>
            <tr>
              <th>Clave</th>
              <th>Nombre</th>
              <th>Tasa</th>
              <th>Tipo</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {taxes.length === 0 ? (
              <tr><td colSpan={6} className="fac-empty">No hay categorías fiscales configuradas.</td></tr>
            ) : (
              taxes.map(t => (
                <tr key={t.$id}>
                  <td className="fac-num-cell">{t.key}</td>
                  <td>{t.name}</td>
                  <td>{t.rate}%</td>
                  <td><span className="fac-type-badge">{t.type}</span></td>
                  <td>{t.active !== false ? <span className="fac-active">Activo</span> : <span className="fac-inactive">Inactivo</span>}</td>
                  <td className="fac-actions-cell">
                    <button className="fac-icon-btn" title="Editar" onClick={() => {
                      setEditingId(t.$id)
                      setShowForm(false)
                      setFormData({ key: t.key, name: t.name, rate: t.rate, type: t.type, active: t.active, notes: t.notes })
                    }}>
                      <Edit3 size={14} />
                    </button>
                    <button className="fac-icon-btn danger" title="Eliminar" onClick={() => onDelete(t.$id)}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="fac-tax-info">
        <AlertTriangle size={16} />
        <span>Las categorías fiscales se aplican a cada ítem de factura. Configurá IVA 21%, IVA 10.5% y Exento como base. Actualmente no se envían a AFIP.</span>
      </div>
    </>
  )
}

// ═══════════════════════════════════════════════════════════
// TAB: PAYMENT RECEIPTS (Recibos de Pago)
// ═══════════════════════════════════════════════════════════

const RECEIPT_METHODS = [
  { value: 'cash', label: 'Efectivo' },
  { value: 'transfer', label: 'Transferencia' },
  { value: 'mercadopago', label: 'MercadoPago' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'other', label: 'Otro' },
]

function PaymentReceiptsTab({ receipts, invoices, showForm, setShowForm, refInvoice, setRefInvoice, processing, onCreateReceipt, onDeleteReceipt }) {
  const [receiptForm, setReceiptForm] = useState({
    invoiceId: '', invoiceNumber: '', clientName: '',
    amount: '', paymentMethod: 'transfer', paymentDate: new Date().toISOString().split('T')[0],
    reference: '', notes: '',
  })

  // When refInvoice is set from the list, populate the form
  useEffect(() => {
    if (refInvoice) {
      setReceiptForm(prev => ({
        ...prev,
        invoiceId: refInvoice.$id,
        invoiceNumber: refInvoice.invoiceNumber || '',
        clientName: refInvoice.clientName || '',
        amount: refInvoice.total || '',
      }))
    }
  }, [refInvoice])

  const totalReceived = receipts.reduce((s, r) => s + (r.amount || 0), 0)
  const unpaidInvoices = invoices.filter(i => i.status === 'sent' || i.status === 'draft')

  const handleSubmit = () => {
    if (!receiptForm.invoiceId || !receiptForm.amount) return
    onCreateReceipt({
      invoiceId: receiptForm.invoiceId,
      invoiceNumber: receiptForm.invoiceNumber,
      clientName: receiptForm.clientName,
      amount: Number(receiptForm.amount),
      paymentMethod: receiptForm.paymentMethod,
      paymentDate: receiptForm.paymentDate,
      reference: receiptForm.reference,
      notes: receiptForm.notes,
    })
  }

  return (
    <>
      {/* KPIs */}
      <div className="fac-kpi-row">
        <div className="fac-kpi">
          <Receipt size={22} className="kpi-icon green" />
          <div>
            <span className="kpi-value">{receipts.length}</span>
            <span className="kpi-label">Recibos Emitidos</span>
          </div>
        </div>
        <div className="fac-kpi">
          <DollarSign size={22} className="kpi-icon blue" />
          <div>
            <span className="kpi-value">{formatCurrency(totalReceived)}</span>
            <span className="kpi-label">Total Cobrado</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="fac-toolbar">
        <div />
        <button className="fac-btn fac-btn-primary" onClick={() => {
          setShowForm(true)
          setRefInvoice(null)
          setReceiptForm({
            invoiceId: '', invoiceNumber: '', clientName: '',
            amount: '', paymentMethod: 'transfer',
            paymentDate: new Date().toISOString().split('T')[0],
            reference: '', notes: '',
          })
        }}>
          <Plus size={16} /> Nuevo Recibo
        </button>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="fac-form-card">
          <h3>Registrar Pago</h3>
          <div className="fac-form-grid">
            <div className="fac-form-group">
              <label>Factura *</label>
              <select
                value={receiptForm.invoiceId}
                onChange={e => {
                  const inv = invoices.find(i => i.$id === e.target.value)
                  setReceiptForm(prev => ({
                    ...prev,
                    invoiceId: e.target.value,
                    invoiceNumber: inv?.invoiceNumber || '',
                    clientName: inv?.clientName || '',
                    amount: inv?.total || '',
                  }))
                }}
              >
                <option value="">Seleccionar factura...</option>
                {unpaidInvoices.map(inv => (
                  <option key={inv.$id} value={inv.$id}>
                    {inv.invoiceNumber} — {inv.clientName} — {formatCurrency(inv.total)}
                  </option>
                ))}
              </select>
            </div>
            <div className="fac-form-group">
              <label>Monto *</label>
              <input type="number" min="0" step="0.01" value={receiptForm.amount}
                onChange={e => setReceiptForm(p => ({ ...p, amount: e.target.value }))} />
            </div>
            <div className="fac-form-group">
              <label>Método de pago</label>
              <select value={receiptForm.paymentMethod}
                onChange={e => setReceiptForm(p => ({ ...p, paymentMethod: e.target.value }))}>
                {RECEIPT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div className="fac-form-group">
              <label>Fecha de pago</label>
              <input type="date" value={receiptForm.paymentDate}
                onChange={e => setReceiptForm(p => ({ ...p, paymentDate: e.target.value }))} />
            </div>
            <div className="fac-form-group">
              <label>Referencia (Nº transf, ID MP, etc.)</label>
              <input type="text" value={receiptForm.reference}
                onChange={e => setReceiptForm(p => ({ ...p, reference: e.target.value }))} />
            </div>
            <div className="fac-form-group">
              <label>Notas</label>
              <input type="text" value={receiptForm.notes}
                onChange={e => setReceiptForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <div className="fac-form-actions">
            <button className="fac-btn" onClick={() => { setShowForm(false); setRefInvoice(null) }}>
              <X size={14} /> Cancelar
            </button>
            <button className="fac-btn fac-btn-primary" onClick={handleSubmit}
              disabled={processing || !receiptForm.invoiceId || !receiptForm.amount}>
              <Save size={14} /> Registrar Pago
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="fac-table-wrap">
        <table className="fac-table">
          <thead>
            <tr>
              <th>Nº Recibo</th>
              <th>Factura</th>
              <th>Cliente</th>
              <th>Monto</th>
              <th>Método</th>
              <th>Fecha</th>
              <th>Referencia</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {receipts.length === 0 ? (
              <tr><td colSpan={8} className="fac-empty">No hay recibos de pago registrados.</td></tr>
            ) : (
              receipts.map(r => (
                <tr key={r.$id}>
                  <td className="fac-num-cell">{r.receiptNumber}</td>
                  <td>{r.invoiceNumber || '—'}</td>
                  <td>{r.clientName || '—'}</td>
                  <td className="fac-total-cell">{formatCurrency(r.amount)}</td>
                  <td><span className="fac-type-badge">{RECEIPT_METHODS.find(m => m.value === r.paymentMethod)?.label || r.paymentMethod}</span></td>
                  <td>{formatDate(r.paymentDate)}</td>
                  <td>{r.reference || '—'}</td>
                  <td className="fac-actions-cell">
                    <button className="fac-icon-btn danger" title="Eliminar" disabled={processing} onClick={() => onDeleteReceipt(r.$id)}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

function AfipTab({ config, meta, invoices, processing, onSaveConfig, onValidate }) {
  const [configForm, setConfigForm] = useState({
    cuit: config?.cuit || '',
    razonSocial: config?.razonSocial || '',
    puntoDeVenta: config?.puntoDeVenta || '1',
    ivaCondition: config?.ivaCondition || '1',
    iibbNumber: config?.iibbNumber || '',
    activityStartDate: config?.activityStartDate || '',
    address: config?.address || '',
    environment: config?.environment || 'testing'
  })
  const [showConfig, setShowConfig] = useState(!config)
  const [validationResult, setValidationResult] = useState(null)
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('')

  const handleSave = async (e) => {
    e.preventDefault()
    await onSaveConfig(configForm)
    setShowConfig(false)
  }

  const handleValidate = async () => {
    if (!selectedInvoiceId) return
    const result = await onValidate(selectedInvoiceId)
    setValidationResult(result)
  }

  const ivaConditions = meta?.ivaConditions || {}
  const pendingInvoices = invoices.filter(i => i.status === 'sent' || i.status === 'draft')

  return (
    <>
      <div className="fac-kpi-row">
        <div className="fac-kpi">
          <span className="fac-kpi-value" style={{ color: config ? '#10b981' : '#ef4444' }}>
            {config ? '✓' : '✗'}
          </span>
          <span className="fac-kpi-label">{config ? 'Configurado' : 'Sin Configurar'}</span>
        </div>
        {config && (
          <>
            <div className="fac-kpi">
              <span className="fac-kpi-value">{config.cuit}</span>
              <span className="fac-kpi-label">CUIT</span>
            </div>
            <div className="fac-kpi">
              <span className="fac-kpi-value">{config.puntoDeVenta}</span>
              <span className="fac-kpi-label">Punto de Venta</span>
            </div>
            <div className="fac-kpi">
              <span className="fac-kpi-value" style={{ fontSize: '0.85rem' }}>
                {ivaConditions[config.ivaCondition] || '—'}
              </span>
              <span className="fac-kpi-label">Condición IVA</span>
            </div>
            <div className="fac-kpi">
              <span className="fac-kpi-value" style={{ color: config.environment === 'production' ? '#10b981' : '#f59e0b' }}>
                {config.environment === 'production' ? 'Producción' : 'Testing'}
              </span>
              <span className="fac-kpi-label">Ambiente</span>
            </div>
          </>
        )}
      </div>

      {/* Config section */}
      <div className="fac-section-header">
        <h3>Configuración AFIP</h3>
        <button className="fac-btn primary" onClick={() => setShowConfig(!showConfig)}>
          {showConfig ? 'Cancelar' : config ? 'Editar Configuración' : 'Configurar AFIP'}
        </button>
      </div>

      {showConfig && (
        <form className="fac-form" onSubmit={handleSave}>
          <div className="fac-form-grid">
            <div className="fac-field">
              <label>CUIT *</label>
              <input value={configForm.cuit} onChange={e => setConfigForm(f => ({ ...f, cuit: e.target.value }))} placeholder="20-12345678-9" required />
            </div>
            <div className="fac-field">
              <label>Razón Social</label>
              <input value={configForm.razonSocial} onChange={e => setConfigForm(f => ({ ...f, razonSocial: e.target.value }))} />
            </div>
            <div className="fac-field">
              <label>Punto de Venta *</label>
              <input type="number" min="1" max="99999" value={configForm.puntoDeVenta} onChange={e => setConfigForm(f => ({ ...f, puntoDeVenta: e.target.value }))} required />
            </div>
            <div className="fac-field">
              <label>Condición IVA *</label>
              <select value={configForm.ivaCondition} onChange={e => setConfigForm(f => ({ ...f, ivaCondition: e.target.value }))}>
                {Object.entries(ivaConditions).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="fac-field">
              <label>Nº IIBB</label>
              <input value={configForm.iibbNumber} onChange={e => setConfigForm(f => ({ ...f, iibbNumber: e.target.value }))} />
            </div>
            <div className="fac-field">
              <label>Inicio de Actividades</label>
              <input type="date" value={configForm.activityStartDate} onChange={e => setConfigForm(f => ({ ...f, activityStartDate: e.target.value }))} />
            </div>
            <div className="fac-field" style={{ gridColumn: 'span 2' }}>
              <label>Domicilio Fiscal</label>
              <input value={configForm.address} onChange={e => setConfigForm(f => ({ ...f, address: e.target.value }))} />
            </div>
            <div className="fac-field">
              <label>Ambiente</label>
              <select value={configForm.environment} onChange={e => setConfigForm(f => ({ ...f, environment: e.target.value }))}>
                <option value="testing">Testing (Homologación)</option>
                <option value="production">Producción</option>
              </select>
            </div>
          </div>
          <div className="fac-form-actions">
            <button type="button" className="fac-btn" onClick={() => setShowConfig(false)}>Cancelar</button>
            <button type="submit" className="fac-btn primary" disabled={processing}>
              {processing ? 'Guardando...' : 'Guardar Configuración'}
            </button>
          </div>
        </form>
      )}

      {/* Invoice validation section */}
      {config && (
        <>
          <div className="fac-section-header" style={{ marginTop: '1.5rem' }}>
            <h3>Validar Factura para AFIP</h3>
          </div>

          <div className="fac-validate-row">
            <div className="fac-field">
              <label>Seleccionar Factura</label>
              <select value={selectedInvoiceId} onChange={e => { setSelectedInvoiceId(e.target.value); setValidationResult(null) }}>
                <option value="">— Elegir factura —</option>
                {pendingInvoices.map(inv => (
                  <option key={inv.$id} value={inv.$id}>
                    {inv.invoiceNumber} — {inv.clientName} — ${inv.total?.toLocaleString('es-AR')}
                  </option>
                ))}
              </select>
            </div>
            <button className="fac-btn primary" disabled={processing || !selectedInvoiceId} onClick={handleValidate} style={{ whiteSpace: 'nowrap' }}>
              <Shield size={14} /> Validar
            </button>
          </div>

          {validationResult && (
            <div className={`fac-validation-result ${validationResult.ready ? 'success' : 'error'}`}>
              <div className="fac-validation-header">
                {validationResult.ready
                  ? <CheckCircle size={20} style={{ color: '#10b981' }} />
                  : <AlertTriangle size={20} style={{ color: '#ef4444' }} />
                }
                <span className="fac-validation-title" style={{ color: validationResult.ready ? '#10b981' : '#ef4444' }}>
                  {validationResult.ready ? 'Factura lista para AFIP' : 'Factura necesita correcciones'}
                </span>
              </div>

              {validationResult.errors?.length > 0 && (
                <div>
                  <strong style={{ color: '#ef4444', fontSize: '0.8rem' }}>Errores:</strong>
                  <ul className="fac-validation-list errors">
                    {validationResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}

              {validationResult.warnings?.length > 0 && (
                <div>
                  <strong style={{ color: '#f59e0b', fontSize: '0.8rem' }}>Advertencias:</strong>
                  <ul className="fac-validation-list warnings">
                    {validationResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              <div className="fac-validation-grid">
                {[
                  { label: 'Tipo Comprobante', value: validationResult.voucherTypeName },
                  { label: 'Punto de Venta', value: String(validationResult.puntoDeVenta).padStart(5, '0') },
                  { label: 'CUIT Emisor', value: validationResult.cuitEmisor },
                  { label: 'Fecha', value: validationResult.date },
                  { label: 'Monto Neto', value: `$${validationResult.netAmount?.toFixed(2)}` },
                  { label: 'IVA 21%', value: `$${validationResult.ivaAmount?.toFixed(2)}` },
                  { label: 'Total', value: `$${validationResult.totalAmount?.toFixed(2)}` },
                  { label: 'Doc. Cliente', value: `${meta?.docTypes?.[validationResult.clientDocType] || '—'}: ${validationResult.clientDocNumber}` },
                  { label: 'Ambiente', value: validationResult.environment === 'production' ? 'Producción' : 'Testing' },
                ].map(item => (
                  <div key={item.label}>
                    <div className="fac-validation-field-label">{item.label}</div>
                    <div className="fac-validation-field-value">{item.value}</div>
                  </div>
                ))}
              </div>

              {validationResult.ready && (
                <div className="fac-validation-note">
                  <p style={{ margin: 0 }}>
                    <Shield size={14} style={{ display: 'inline', verticalAlign: '-2px', marginRight: '0.25rem' }} />
                    La factura cumple con los requisitos de AFIP. Para emitir el CAE se requiere el certificado digital
                    y la integración con los web services de AFIP (WSFE/WSFEX). Esta validación prepara los datos necesarios.
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  )
}
